import {
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  SlideFade,
  Stack,
  Text,
  useColorModeValue,
  Select,
  Box,
  Image,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  useDisclosure,
  Spinner,
  HStack,
  useToast,
  IconButton
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import {
  AiOutlinePhone,
  AiOutlineCamera,
  AiOutlineCheckCircle,
  AiOutlineCopy
} from "react-icons/ai";
import Container from "../../components/Container";
import { PrimaryButton } from "../../components/CustomButton";
import { errorNotifier } from "../../components/NotificationHandler";
import { Link } from "react-router-dom";
import { COLORS } from "../../constants/colors";
import { PUBLIC_PATHS } from "../../app/constants";
import { fetchStreetsData, signupTenant } from "./service/signup";

const { LOGIN } = PUBLIC_PATHS;

const Signup = () => {
  const cardBg = useColorModeValue(COLORS.primary, COLORS.white);
  const pageBg = useColorModeValue(COLORS.white, COLORS.black);
  const toast = useToast();

  // Signup form states
  const [streetId, setStreetId] = useState("");
  const [houseId, setHouseId] = useState(""); // Changed from houseNumber to houseId
  const [displayHouseNumber, setDisplayHouseNumber] = useState(""); // For display purposes
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [landlordName, setLandlordName] = useState("");
  const [landlordPhone, setLandlordPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  // Backend data states
  const [streetsData, setStreetsData] = useState({});
  const [housesMap, setHousesMap] = useState({}); // Map to store house number -> house ID mapping
  const [loadingStreets, setLoadingStreets] = useState(true);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isRedirecting, setIsRedirecting] = useState(false);

  const managers = [
    { name: "Adebayo", phone: "08023887167" },
    { name: "James", phone: "09168324043" },
    { name: "Gbolahan", phone: "08163813333" },
    { name: "Abari", phone: "08023874139" }
  ];

  // Fetch streets and house numbers from backend on component mount
  useEffect(() => {
    console.log("Starting to fetch streets data...");
    let isSubscribed = true;

    const loadStreetsData = async () => {
      try {
        await fetchStreetsData(
          (data) => {
            if (isSubscribed) {
              console.log("Received streets data:", data);
              setStreetsData(data);

              // Create a house mapping for each street using the data returned by fetchStreetsData
              const houseMapping = {};
              Object.entries(data).forEach(([streetId, streetData]) => {
                houseMapping[streetId] = streetData.houseDetails || {};
              });
              setHousesMap(houseMapping);
            }
          },
          (loading) => {
            if (isSubscribed) {
              setLoadingStreets(loading);
            }
          }
        );
      } catch (error) {
        console.error("Failed to fetch streets data:", error);
        if (isSubscribed) {
          setLoadingStreets(false);
          errorNotifier("Could not load streets data.");
        }
      }
    };

    loadStreetsData();

    return () => {
      isSubscribed = false;
    };
  }, []);

  // Debug log when streetsData changes
  useEffect(() => {
    console.log("Current streetsData state:", streetsData);
  }, [streetsData]);

  const handleStreetChange = (e) => {
    const selectedStreetId = e.target.value;
    setStreetId(selectedStreetId); // Set the street ID to be sent to the backend
    setHouseId(""); // Reset house ID when street changes
    setDisplayHouseNumber(""); // Reset display house number
  };

  const handleHouseChange = (e) => {
    const selectedHouseNumber = e.target.value;
    setDisplayHouseNumber(selectedHouseNumber);

    // Get the house ID from our mapping
    if (housesMap[streetId] && housesMap[streetId][selectedHouseNumber]) {
      setHouseId(housesMap[streetId][selectedHouseNumber]);
    } else {
      // Fallback to using the house number if mapping fails
      setHouseId(selectedHouseNumber);
      console.warn(
        "House ID mapping not found for",
        selectedHouseNumber,
        "using house number instead"
      );
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!selectedImage) {
      errorNotifier("Please upload a profile image");
      return;
    }

    if (
      !houseId ||
      !streetId ||
      !tenantName ||
      !tenantPhone ||
      !email ||
      !designation
    ) {
      errorNotifier("Please fill in all fields");
      return;
    }

    // Validate landlord fields when designation is tenant
    if (designation === "tenant" && (!landlordName || !landlordPhone)) {
      errorNotifier("Please provide landlord name and phone number");
      return;
    }

    const formData = new FormData();
    formData.append("houseId", houseId);
    formData.append("streetId", streetId);
    formData.append("houseNumber", displayHouseNumber);
    formData.append("tenantName", tenantName);
    formData.append("tenantPhone", tenantPhone);
    formData.append("email", email);
    formData.append("designation", designation);
    formData.append("profileImage", selectedImage);

    // Add landlord details if user is a tenant
    if (designation === "tenant") {
      formData.append("landlord", landlordName);
      formData.append("landlordPhone", landlordPhone);
    }

    const success = await signupTenant(formData, setLoading);
    if (success) {
      onOpen();
    }
  };

  const handleContinue = () => {
    setIsRedirecting(true);
    onClose();
    // Add a small delay to show the loading state
    setTimeout(() => {
      window.location.href = LOGIN;
    }, 1000);
  };

  const handleModalClose = () => {
    setIsRedirecting(true);
    onClose();
    // Add a small delay to show the loading state
    setTimeout(() => {
      window.location.href = LOGIN;
    }, 1000);
  };

  const handleCopyPhone = (phoneNumber, name) => {
    navigator.clipboard.writeText(phoneNumber).then(() => {
      toast({
        title: "Phone number copied!",
        description: `Mr. ${name}'s phone number copied to clipboard`,
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top"
      });
    }).catch(() => {
      toast({
        title: "Copy failed",
        description: "Please copy the phone number manually",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top"
      });
    });
  };

  const handleCallPhone = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  return (
    <SlideFade in={true} offsetX="-5000px">
      {!isRedirecting ? (
        <Container bg={pageBg}>
          <Stack w="100%" as="section" pt="15vh" h="100%">
            <Text color={cardBg} fontWeight="600" fontSize="2.7rem">
              Sign Up
            </Text>
            <Text color={cardBg} fontWeight="500" fontSize="1.2rem">
              Kindly fill up the details below
            </Text>
            <FormControl w="100%">
              {/* Image Upload Section */}
              <Box position="relative">
                <Box
                  position="relative"
                  w="120px"
                  h="120px"
                  mx="auto"
                  my="6"
                  borderRadius="full"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={handleImageClick}
                  border="2px solid"
                  borderColor={COLORS.green}
                  transition="all 0.3s"
                  _hover={{
                    transform: "scale(1.05)",
                    boxShadow: "lg"
                  }}
                >
                  {selectedImage ? (
                    <>
                      <Image
                        src={URL.createObjectURL(selectedImage)}
                        alt="Profile"
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                      <Box
                        position="absolute"
                        bottom="0"
                        w="100%"
                        bg="rgba(0, 0, 0, 0.6)"
                        color="white"
                        textAlign="center"
                        fontSize="xs"
                        py="1"
                        fontWeight="bold"
                        transition="all 0.3s"
                        _hover={{
                          bg: "rgba(0, 0, 0, 0.8)"
                        }}
                      >
                        Change Image
                      </Box>
                    </>
                  ) : (
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      textAlign="center"
                      color={COLORS.green}
                    >
                      <Icon as={AiOutlineCamera} boxSize="8" />
                      <Text fontSize="sm" fontWeight="bold" mt="2">
                        Upload Photo
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt="1">
                        Required
                      </Text>
                    </Box>
                  )}
                  <Input
                    type="file"
                    ref={fileInputRef}
                    display="none"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Box>
                <Text
                  position="absolute"
                  top="-8px"
                  right="calc(50% - 60px)"
                  color="red.500"
                  fontWeight="600"
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                >
                  *
                </Text>
                <Text textAlign="center" fontSize="sm" color="gray.500" mb="4">
                  Upload a clear photo of yourself
                </Text>
              </Box>

              <Flex my="7" gap="4">
                {/* Street Select */}
                <Box position="relative" flex="1">
                  <InputGroup flex="1">
                    <Select
                      {...inputStyles}
                      borderColor={COLORS.green}
                      placeholder="Select Street"
                      _placeholder={{ color: COLORS.green }}
                      value={streetId}
                      onChange={handleStreetChange}
                      isDisabled={loadingStreets}
                    >
                      {Object.values(streetsData)
                        .sort((a, b) =>
                          a.streetName.localeCompare(b.streetName)
                        )
                        .map(({ streetName, streetId }) => (
                          <option key={streetId} value={streetId}>
                            {streetName}
                          </option>
                        ))}
                    </Select>
                  </InputGroup>

                  <Text
                    position="absolute"
                    top="-8px"
                    right="8px"
                    color="red.500"
                    fontWeight="600"
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  >
                    *
                  </Text>
                </Box>

                {/* House Number Select */}
                <Box position="relative" flex="1">
                  <InputGroup flex="1">
                    <Select
                      {...inputStyles}
                      borderColor={COLORS.green}
                      placeholder="Select House"
                      _placeholder={{ color: COLORS.green }}
                      value={displayHouseNumber}
                      onChange={handleHouseChange}
                      isDisabled={!streetId}
                    >
                      {streetId &&
                        streetsData[streetId]?.houses.map((number) => (
                          <option key={number} value={number}>
                            {number}
                          </option>
                        ))}
                    </Select>
                  </InputGroup>
                  <Text
                    position="absolute"
                    top="-8px"
                    right="8px"
                    color="red.500"
                    fontWeight="600"
                    fontSize="24px"
                    bg={pageBg}
                    px="2px"
                  >
                    *
                  </Text>
                </Box>
              </Flex>

              {/* Designation Select Field */}
              <Box position="relative" flex="1">
                <InputGroup my="7">
                  <Select
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Designation"
                    _placeholder={{ color: COLORS.green }}
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                  >
                    <option value="tenant">Tenant</option>
                    <option value="landlord">Landlord</option>
                    <option value="agent">Agent</option>
                    <option value="business">Business</option>
                  </Select>
                </InputGroup>
                <Text
                  position="absolute"
                  top="-8px"
                  right="8px"
                  color="red.500"
                  fontWeight="600"
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                >
                  *
                </Text>
              </Box>

              {/* Landlord Details - Show only when Tenant is selected */}
              {designation === "tenant" && (
                <>
                  <Box position="relative" flex="1">
                    <InputGroup my="7">
                      <Input
                        {...inputStyles}
                        borderColor={COLORS.green}
                        placeholder="Landlord Name"
                        _placeholder={{ color: COLORS.green }}
                        value={landlordName}
                        onChange={(e) => setLandlordName(e.target.value)}
                      />
                    </InputGroup>
                    <Text
                      position="absolute"
                      top="-8px"
                      right="8px"
                      color="red.500"
                      fontWeight="600"
                      fontSize="24px"
                      bg={pageBg}
                      px="2px"
                    >
                      *
                    </Text>
                  </Box>

                  <Box position="relative" flex="1">
                    <InputGroup my="7">
                      <Input
                        {...inputStyles}
                        borderColor={COLORS.green}
                        placeholder="Landlord Phone Number"
                        _placeholder={{ color: COLORS.green }}
                        value={landlordPhone}
                        type="tel"
                        onChange={(e) => setLandlordPhone(e.target.value)}
                      />
                      <InputLeftElement children={<AiOutlinePhone />} mt="10px" />
                    </InputGroup>
                    <Text
                      position="absolute"
                      top="-8px"
                      right="8px"
                      color="red.500"
                      fontWeight="600"
                      fontSize="24px"
                      bg={pageBg}
                      px="2px"
                    >
                      *
                    </Text>
                  </Box>
                </>
              )}

              {/* Full Name Input Field */}
              <Box position="relative" flex="1">
                <InputGroup my="7">
                  <Input
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Full Name"
                    _placeholder={{ color: COLORS.green }}
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                  />
                </InputGroup>
                <Text
                  position="absolute"
                  top="-8px"
                  right="8px"
                  color="red.500"
                  fontWeight="600"
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                >
                  *
                </Text>
              </Box>

              {/* Phone Number Input Field */}
              <Box position="relative" flex="1">
                <InputGroup my="7">
                  <Input
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Phone Number"
                    _placeholder={{ color: COLORS.green }}
                    value={tenantPhone}
                    type="number"
                    onChange={(e) => setTenantPhone(e.target.value)}
                  />
                  <InputLeftElement children={<AiOutlinePhone />} mt="10px" />
                </InputGroup>
                <Text
                  position="absolute"
                  top="-8px"
                  right="8px"
                  color="red.500"
                  fontWeight="600"
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                >
                  *
                </Text>
              </Box>
              {/* Email Input Field */}
              <Box position="relative" flex="1">
                <InputGroup my="7">
                  <Input
                    {...inputStyles}
                    borderColor={COLORS.green}
                    placeholder="Email"
                    _placeholder={{ color: COLORS.green }}
                    value={email}
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </InputGroup>
                <Text
                  position="absolute"
                  top="-8px"
                  right="8px"
                  color="red.500"
                  fontWeight="600"
                  fontSize="24px"
                  bg={pageBg}
                  px="2px"
                >
                  *
                </Text>
              </Box>
              

              {/* Signup Button */}
              <PrimaryButton
                loadingText="Signing you up"
                btnText="Sign up"
                isLoading={loading}
                onClick={handleSignup}
                h="60px"
                fontSize="18px"
                rounded="2xl"
              />

              {/* Login Link */}
              <Text
                fontSize="16px"
                textAlign="center"
                fontWeight={600}
                color={cardBg}
                mb="20px"
              >
                Already have an account?{" "}
                <Link
                  to={LOGIN}
                  style={{ color: COLORS.green, textDecoration: "underline" }}
                >
                  Log In
                </Link>
              </Text>
            </FormControl>
          </Stack>
        </Container>
      ) : (
        <Flex w="100%" h="100vh" justify="center" align="center" bg={pageBg}>
          <Stack spacing={4} align="center">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color={COLORS.green}
              size="xl"
            />
            <Text color={COLORS.green} fontWeight="medium">
              Redirecting to login...
            </Text>
          </Stack>
        </Flex>
      )}

      {/* Success Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size="md" closeOnOverlayClick={true} >
        <ModalOverlay />
        <ModalContent bg={COLORS.green} color="white" borderRadius="xl" mt="250">
          <ModalBody p={8}>
            <Stack spacing={6} align="center" textAlign="center">
              <Icon as={AiOutlineCheckCircle} boxSize="16" />
              <Text fontSize="2xl" fontWeight="bold">
                Well Done!
              </Text>
              <Text fontSize="lg" fontWeight="semibold">
                Please reach out to the Estate Manager for approval before
                attempting to log in.
              </Text>
              
              {/* Estate Manager Contact Section */}
              <Box
                bg="rgba(255, 255, 255, 0.1)"
                borderRadius="lg"
                p={4}
                w="full"
              >
                <Text fontSize="md" fontWeight="semibold" mb={3}>
                  Contact Estate Managers:
                </Text>
                <Stack spacing={2}>
                  {managers.map((manager) => (
                    <HStack key={manager.name} justify="center" spacing={2}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {manager.name}:
                      </Text>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        fontFamily="monospace"
                        bg="rgba(255, 255, 255, 0.2)"
                        px={3}
                        py={2}
                        borderRadius="md"
                        flex="1"
                        textAlign="center"
                      >
                        {manager.phone}
                      </Text>
                      <IconButton
                        aria-label="Copy phone number"
                        icon={<AiOutlineCopy />}
                        size="sm"
                        bg="white"
                        color={COLORS.green}
                        _hover={{ bg: "gray.100" }}
                        onClick={() => handleCopyPhone(manager.phone, manager.name)}
                      />
                      <IconButton
                        aria-label="Call manager"
                        icon={<AiOutlinePhone />}
                        size="sm"
                        bg="white"
                        color={COLORS.green}
                        _hover={{ bg: "gray.100" }}
                        onClick={() => handleCallPhone(manager.phone)}
                      />
                    </HStack>
                  ))}
                </Stack>
              </Box>

              <Text fontSize="sm">
                Your account has been created successfully. Check your email for
                your default User Code/Password. You can change this at your
                first login.
              </Text>
              
              <Button
                bg="white"
                color={COLORS.green}
                _hover={{ bg: "gray.100" }}
                onClick={handleContinue}
                size="lg"
                w="full"
                mt={4}
                isLoading={isRedirecting}
                loadingText="Redirecting..."
              >
                Continue to Login
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </SlideFade>
  );
};

export default Signup;

export const inputStyles = {
  bg: "white",
  rounded: "2xl",
  h: "60px",
  color: "#262636"
};